import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { CheckCircle2, Trash2, Gift } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { StarRating, StarInput } from "./StarRating";
import { ProductTestimonials } from "./ProductTestimonials";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";

export const ProductReviews = ({ productId, onRatingChange }) => {
  const { user, login } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [deleteReview, setDeleteReview] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [eligibility, setEligibility] = useState(null);
  const canDelete = !!(user?.is_super_admin || user?.is_general_admin);

  const loadEligibility = useCallback(() => {
    if (!user) return;
    api.get(`/products/${productId}/reviews/eligibility`).then(({ data }) => setEligibility(data)).catch(() => {});
  }, [productId, user]);

  useEffect(() => { loadEligibility(); }, [loadEligibility]);

  const load = useCallback(() => {
    api.get(`/products/${productId}/reviews`).then(({ data }) => setReviews(data)).catch(() => {});
  }, [productId]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (rating < 1) { toast.error("Please select a star rating"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/products/${productId}/reviews`, { rating, comment });
      toast.success(data.points_awarded > 0
        ? `Review posted — you earned ${data.points_awarded} reward points!`
        : "Thanks for your review!");
      setComment("");
      setRating(0);
      load();
      loadEligibility();
      onRatingChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit review");
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDeleteReview = async () => {
    setDeleting(true);
    try {
      await api.delete(`/products/${productId}/reviews/${deleteReview.id}`);
      toast.success("Review deleted");
      setDeleteReview(null);
      load();
      onRatingChange?.();
    } catch {
      toast.error("Failed to delete review");
    } finally {
      setDeleting(false);
    }
  };

  const myReview = user && reviews.find((r) => r.user_id === user.user_id);

  let submitLabel = myReview ? "Update Review" : "Submit Review";
  if (submitting) submitLabel = "Submitting…";

  return (
    <div data-testid="product-reviews">
      <h2 className="font-display text-lg uppercase tracking-wide text-[#1A1A1A] mb-6">
        Reviews {reviews.length > 0 && <span className="text-[#6E675F] text-base">({reviews.length})</span>}
      </h2>

      {/* Write / edit review */}
      {user ? (
        eligibility?.eligible ? (
          <div className="border border-[#DDD7CF] rounded-lg bg-white p-6 mb-8" data-testid="review-form">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
              <p className="text-sm font-semibold text-[#1A1A1A]">{myReview ? "Update your review" : "Write a review"}</p>
              {!eligibility.points_awarded && (
                <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#8A6D2F] bg-[#F6F1E7] border border-[#D4B36A]/50 rounded-none px-3 py-1" data-testid="review-points-incentive">
                  <Gift className="w-3 h-3" /> Earn {eligibility.points_value} reward points for your review
                </span>
              )}
            </div>
            <StarInput value={rating} onChange={setRating} />
            <Textarea
              data-testid="review-comment-input"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              placeholder="Share your experience with this compound (purity, packaging, results in your research)…"
              className="mt-4"
            />
            <button
              data-testid="submit-review-button"
              onClick={submit}
              disabled={submitting}
              className="mt-4 bg-[#8C7B6B] text-white text-xs tracking-[0.15em] uppercase font-semibold px-6 py-3 rounded-none hover:bg-[#75665A] transition-colors disabled:opacity-60"
            >
              {submitLabel}
            </button>
          </div>
        ) : (
          eligibility && (
            <div className="border border-[#DDD7CF] rounded-lg bg-white p-6 mb-8" data-testid="review-gated-message">
              <p className="text-sm text-[#6E675F]">
                <Gift className="w-3.5 h-3.5 inline mr-1.5 text-[#8C7B6B]" />
                Reviews are open to verified buyers. Order this product and once your order is completed,
                you can leave a review and earn <strong className="text-[#1A1A1A]">20 reward points</strong>.
              </p>
            </div>
          )
        )
      ) : (
        <div className="border border-[#DDD7CF] rounded-lg bg-white p-6 mb-8 text-center">
          <p className="text-sm text-[#6E675F] mb-3">Sign in to leave a review.</p>
          <button data-testid="review-signin-button" onClick={login} className="text-xs tracking-[0.15em] uppercase font-semibold text-[#8C7B6B] hover:text-[#75665A]">
            Sign In with Google
          </button>
        </div>
      )}

      {reviews.length === 0 ? (
        <>
          <p className="text-sm text-[#6E675F]" data-testid="no-reviews-message">No reviews yet — be the first to review this product.</p>
          <ProductTestimonials productId={productId} />
        </>
      ) : (
        <div className="space-y-5" data-testid="reviews-list">
          {reviews.map((r) => (
            <div key={r.id} className="border-b border-[#DDD7CF] pb-5" data-testid={`review-${r.id}`}>
              <div className="flex items-center gap-3 flex-wrap">
                <StarRating value={r.rating} />
                <span className="text-sm font-semibold text-[#1A1A1A]">{r.user_name}</span>
                {r.location && <span className="text-xs text-[#6E675F]" data-testid={`review-location-${r.id}`}>· {r.location}</span>}
                {r.verified_purchase && (
                  <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.1em] uppercase font-semibold text-[#2D6A4F]">
                    <CheckCircle2 className="w-3 h-3" /> Verified Purchase
                  </span>
                )}
                <span className="text-xs text-[#6E675F] ml-auto">{new Date(r.created_at).toLocaleDateString()}</span>
                {canDelete && (
                  <button
                    data-testid={`delete-review-button-${r.id}`}
                    onClick={() => setDeleteReview(r)}
                    className="p-1 text-red-700 hover:text-red-900 transition-colors"
                    aria-label="Delete review"
                    title="Delete review"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
              {r.comment && <p className="text-sm text-[#6E675F] mt-2 leading-relaxed">{r.comment}</p>}
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!deleteReview} onOpenChange={(open) => !open && setDeleteReview(null)}>
        <DialogContent data-testid="delete-review-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-wide">Delete Review</DialogTitle>
            <DialogDescription>
              Permanently delete {deleteReview?.user_name}&apos;s review? The product rating will be recalculated. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" data-testid="delete-review-cancel" onClick={() => setDeleteReview(null)}>Cancel</Button>
            <Button data-testid="delete-review-confirm" onClick={confirmDeleteReview} disabled={deleting} className="bg-red-600 text-white hover:bg-red-700">
              <Trash2 className="w-4 h-4 mr-1" /> {deleting ? "Deleting…" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
